import { DateTime } from 'luxon'
import { useNavigate, useParams } from 'react-router-dom'
import { useStyles } from './FlyoutOverview.styles'
import {
  useAccountId,
  LoadingStateComponent,
  useCurrentCloudProvider,
  useScreenSize,
  LoadingTypes,
  isLoading as loading,
  upcomingRenewalsOverviewInfoText,
} from '@labrav/common'
import {
  revenueOverviewInfoText,
  revenueByProductOverviewInfoText,
  churnedSubscriptionOverviewInfoText,
  activeSubscriptionOverviewInfoText,
  overviewLineGraphDateRange,
  offerAttentionOverviewInfoText,
  revenueViaISVOverviewInfoText,
  revenueByTypeOverviewInfoText,
} from '../../common/utils/constants'
import { OverviewSectionWrapper } from './OverviewSectionWrapper/OverviewSectionWrapper'
import { Grid } from '@mui/material'
import {
  faChartMixed,
  faFileContract,
  faChartPie,
  faTriangleExclamation,
  faCalendar,
} from '@fortawesome/pro-regular-svg-icons'

import { RevenueOverviewChart } from './RevenueOverviewChart/RevenueOverviewChart'
import { ActiveSubscriptionsChart } from './ActiveSubscriptionsChart/ActiveSubscriptionsChart'
import { ChurnedSubscriptionsChart } from './ChurnedSubscriptionsChart/ChurnedSubscriptionsChart'
import { OffersForBuyers } from './OffersComponent/OffersForBuyers'
import { RevenueByProductChart } from './RevenueByProductChart/RevenueByProductChart'

import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from '../../store/hooks'
import { useSyncFlyoutRouteParams } from '../../common/utils/hooks/useSyncFlyoutRouteParams'
import { getFlyoutOverview } from '../../modules/flyoutOverview/action'
import { OffersAttention } from './OffersAttention/OffersAttention'
import { UpcomingRenewals } from './UpcomingRenewals/UpcomingRenewals'
import { ListingCreationPage } from '../../common/components/ListingCreationPage/ListingCreationPage'
import { useFlyOutProductsBasedOnSelectedCloud } from '../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { FlyoutOnboardingPartnershipType } from '../../modules/flyOutOnboarding/types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { subscriptionsRangeStatus } from '../../utils/subscriptionsFilterMapping'
import { Overview } from '../../modules/flyoutOverview/types'
import { RevenueViaISVChart } from './RevenueViaISVChart/RevenueViaISVChart'
import { RevenueByTypeChart } from './RevenueByTypeChart/RevenueByTypeChart'
import { ResellerComponent } from './ResellerComponent/ResellerComponent'

export const FlyoutOverview = () => {
  const { classes } = useStyles()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { screenWidth } = useScreenSize()
  const { cloud: routeCloud, accountId: routeAccountId } = useParams<{
    cloud?: string
    accountId?: string
  }>()
  const selectedCloud = useCurrentCloudProvider()
  const isBelowMediumScreen = screenWidth <= 1230
  const defaultAccountId = useAccountId({ product: 'flyout' }) || ''
  const accountId = routeAccountId || defaultAccountId

  // Sync route params to selectedAccountIds for FLYOUT - single source of truth
  useSyncFlyoutRouteParams()

  const isOverviewPageLoading = useSelector<boolean>(state =>
    loading(state.loading)(LoadingTypes.FLYOUT_OVERVIEW)
  )
  const isProductsLoading = useSelector<boolean>(state =>
    loading(state.loading)(LoadingTypes.GENERAL)
  )
  const { products: productRows } = useFlyOutProductsBasedOnSelectedCloud()
  const goToPrivateOffers = () => {
    const cloudPath = routeCloud?.toLowerCase() || selectedCloud.toLowerCase()
    const privateOffersPath =
      routeCloud && accountId
        ? `/flyout/${cloudPath}/${accountId}/offers-authorizations/private-offers`
        : '/flyout/offers-authorizations/private-offers'
    navigate(privateOffersPath)
  }
  const goToRevenueInsights = () => {
    const cloudPath = routeCloud?.toLowerCase() || selectedCloud.toLowerCase()
    const revenuePath =
      routeCloud && accountId
        ? `/flyout/${cloudPath}/${accountId}/revenue`
        : '/flyout/revenue'
    navigate(revenuePath)
  }
  const goToSubscriptions = (status: subscriptionsRangeStatus) => {
    const cloudPath = routeCloud?.toLowerCase() || selectedCloud.toLowerCase()
    const subscriptionsPath =
      routeCloud && accountId
        ? `/flyout/${cloudPath}/${accountId}/subscriptions`
        : '/flyout/subscriptions'
    navigate(subscriptionsPath, {
      state: {
        status: status,
      },
    })
  }
  const { offersForBuyers } = useSelector<Overview>(
    state => state.flyoutOverview?.overview
  )
  const { givenResellerAgreementCount, receivedResellerAgreementCount } =
    useSelector<Overview>(state => state.flyoutOverview?.overview)
  const awsPartnershipType = useSelector(
    state => state.AccountData.user.partnerData?.partnershipType
  )

  const partnershipType = useMemo(() => {
    if (!awsPartnershipType) return null

    const partnershipTypes = Array.isArray(awsPartnershipType)
      ? awsPartnershipType
      : [awsPartnershipType]

    const hasConsulting = partnershipTypes.includes('CONSULTING_PARTNER')
    const hasTechnology = partnershipTypes.includes('TECHNOLOGY_PARTNER')

    if (hasConsulting && hasTechnology) {
      return [
        FlyoutOnboardingPartnershipType.CP,
        FlyoutOnboardingPartnershipType.ISV,
      ]
    } else if (hasConsulting) {
      return FlyoutOnboardingPartnershipType.CP
    } else if (hasTechnology) {
      return FlyoutOnboardingPartnershipType.ISV
    }
    return null
  }, [awsPartnershipType])

  const showOnlyForIsv =
    partnershipType && partnershipType === FlyoutOnboardingPartnershipType.ISV
  const showOnlyForCP =
    partnershipType && partnershipType === FlyoutOnboardingPartnershipType.CP
  const today = DateTime.now().toFormat(overviewLineGraphDateRange)
  const lastYearDate = DateTime.now()
    .minus({ years: 1 })
    .plus({ months: 1 })
    .toFormat(overviewLineGraphDateRange)

  const dateRangeDescription = `${lastYearDate} - ${today}`
  useEffect(() => {
    if (selectedCloud === 'AWS') {
      dispatch(
        getFlyoutOverview({
          partnerId: accountId,
        })
      )
    }
  }, [accountId, selectedCloud])

  return (
    <>
      {isOverviewPageLoading && isProductsLoading ? (
        <>
          <LoadingStateComponent
            heading="Loading your overview page"
            description="It may take a few moments, please wait..."
          />
        </>
      ) : (
        <>
          {productRows &&
          !productRows?.length &&
          !isProductsLoading &&
          !isOverviewPageLoading ? (
            <ListingCreationPage />
          ) : (
            <div className={classes.root} data-testid={'overview-page'}>
              <div className={classes.description}>
                <FontAwesomeIcon
                  icon={faCalendar}
                  style={{ width: '18px', height: '14px', marginRight: '8px' }}
                />
                The graphs below show data from last 12 months ({lastYearDate}{' '}
                to {today})
              </div>
              <Grid container className={classes.content} spacing={4}>
                <Grid
                  className={classes.container}
                  size={{
                    xs: 12,
                    sm: 12,
                    md: isBelowMediumScreen ? 12 : 6,
                    lg: 6,
                  }}
                >
                  <OverviewSectionWrapper
                    children={
                      <RevenueOverviewChart
                        dateRangeDescription={dateRangeDescription}
                      />
                    }
                    heading="Revenue"
                    icon={faChartMixed}
                    actionLabel={'View details'}
                    handleAction={goToRevenueInsights}
                    infoTooltip={revenueOverviewInfoText}
                  />

                  {showOnlyForCP && (
                    <OverviewSectionWrapper
                      children={<RevenueViaISVChart />}
                      heading="Revenue via ISV"
                      icon={faChartPie}
                      actionLabel={'View details'}
                      handleAction={goToRevenueInsights}
                      infoTooltip={revenueViaISVOverviewInfoText}
                    />
                  )}
                  {showOnlyForIsv && (
                    <OverviewSectionWrapper
                      children={<RevenueByProductChart />}
                      heading="Revenue by product"
                      icon={faChartPie}
                      actionLabel={'View details'}
                      handleAction={goToRevenueInsights}
                      infoTooltip={revenueByProductOverviewInfoText}
                    />
                  )}
                  <OverviewSectionWrapper
                    children={<RevenueByTypeChart />}
                    heading="Revenue by offer type"
                    icon={faChartPie}
                    actionLabel={'View details'}
                    handleAction={goToRevenueInsights}
                    infoTooltip={revenueByTypeOverviewInfoText}
                  />
                  <Grid container spacing={3}>
                    <Grid size={showOnlyForCP && showOnlyForIsv ? 12 : 6}>
                      <OverviewSectionWrapper
                        children={
                          <OffersForBuyers
                            offersForBuyers={offersForBuyers}
                            link="/flyout/offers-authorizations/private-offers"
                          />
                        }
                        heading="Offers for buyers"
                        headerAlignment={'normal'}
                      />
                    </Grid>

                    {showOnlyForCP && (
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                          md: 6,
                        }}
                      >
                        <OverviewSectionWrapper
                          children={
                            <ResellerComponent
                              resellerAgreementCount={
                                receivedResellerAgreementCount?.total || 0
                              }
                              link="/flyout/reseller-authorizations/received"
                            />
                          }
                          heading="Received Reseller authorizations"
                          headerAlignment={'normal'}
                        />
                      </Grid>
                    )}
                    {showOnlyForIsv && (
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                          md: 6,
                        }}
                      >
                        <OverviewSectionWrapper
                          children={
                            <ResellerComponent
                              resellerAgreementCount={
                                givenResellerAgreementCount?.total || 0
                              }
                              link="/flyout/reseller-authorizations/sent"
                            />
                          }
                          heading="Given Reseller authorizations"
                          headerAlignment={'normal'}
                        />
                      </Grid>
                    )}
                  </Grid>

                  <div>
                    <OverviewSectionWrapper
                      children={
                        <ActiveSubscriptionsChart
                          dateRangeDescription={dateRangeDescription}
                        />
                      }
                      heading="Active subscriptions"
                      icon={faFileContract}
                      actionLabel={'View details'}
                      handleAction={() =>
                        goToSubscriptions(subscriptionsRangeStatus.ACTIVE)
                      }
                      infoTooltip={activeSubscriptionOverviewInfoText}
                      removeBottomBorder={true}
                    />
                    <div className={classes.horizontalDivider}></div>
                    <OverviewSectionWrapper
                      children={
                        <ChurnedSubscriptionsChart
                          dateRangeDescription={dateRangeDescription}
                        />
                      }
                      heading="Churned subscriptions"
                      icon={faFileContract}
                      actionLabel={'View details'}
                      handleAction={() =>
                        goToSubscriptions(subscriptionsRangeStatus.EXPIRED)
                      }
                      infoTooltip={churnedSubscriptionOverviewInfoText}
                      removeTopBorder={true}
                    />
                  </div>
                  {/* TODO Later
                    <OverviewSectionWrapper
                    children={<AccepetedOfferBySales />}
                    heading="Accepted Private offers by sales reps"
                    icon={faRankingStar}
                    actionLabel={'View details'}
                    handleAction={goToPrivateOffers}
                    infoTooltip={accepetedOfferBySalesOverviewInfoText}
                  /> */}
                </Grid>
                <Grid
                  className={classes.container}
                  size={{
                    xs: 12,
                    sm: 12,
                    md: isBelowMediumScreen ? 12 : 6,
                    lg: 6,
                  }}
                >
                  <OverviewSectionWrapper
                    heading="Offers needs your attention"
                    children={<OffersAttention />}
                    icon={faTriangleExclamation}
                    infoTooltip={offerAttentionOverviewInfoText}
                    actionLabel={'View all'}
                    handleAction={goToPrivateOffers}
                  />
                  <OverviewSectionWrapper
                    heading="Upcoming renewals in next 2 months"
                    children={<UpcomingRenewals />}
                    icon={faTriangleExclamation}
                    infoTooltip={upcomingRenewalsOverviewInfoText}
                    actionLabel={'View all'}
                    handleAction={() =>
                      goToSubscriptions(subscriptionsRangeStatus.ACTIVE)
                    }
                  />
                </Grid>
              </Grid>
            </div>
          )}
        </>
      )}
    </>
  )
}
